import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useWorkspaceData } from "@/hooks/use-workspace-data";
/**
 * Workspace sidebar — quick links + folder tree.
 *
 * Folders are rendered as a flat list (root → first-level) for Stage 2. A
 * proper tree view (with collapsible parents) lands alongside the full
 * folder CRUD API.
 */
import { ChevronRight, Folder, Home, Settings } from "lucide-react";
import { Link } from "react-router-dom";

export interface WorkspaceSidebarProps {
  selectedFolderId: string | null;
  onSelect: (folderId: string | null) => void;
}

export function WorkspaceSidebar({
  selectedFolderId,
  onSelect,
}: WorkspaceSidebarProps): JSX.Element {
  const folders = useWorkspaceData((s) => s.folders);
  const projects = useWorkspaceData((s) => s.projects);

  const rootProjects = projects.filter((p) => !p.folder_id);

  return (
    <aside className="flex h-full w-60 shrink-0 flex-col border-r border-border bg-card">
      <div className="p-3">
        <h2 className="px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Navigation
        </h2>
      </div>
      <nav className="space-y-0.5 px-3">
        <SidebarItem
          active={selectedFolderId === null}
          icon={<Home className="h-4 w-4" />}
          label="All projects"
          count={projects.length}
          onClick={() => onSelect(null)}
        />
        <SidebarItem
          active={false}
          icon={<Folder className="h-4 w-4" />}
          label="Uncategorized"
          count={rootProjects.length}
          onClick={() => onSelect("__root__")}
          disabled
        />
      </nav>

      <Separator className="my-3" />

      <div className="px-3">
        <h2 className="px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Folders
        </h2>
      </div>
      <div className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-3">
        {folders.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">No folders yet.</p>
        ) : (
          folders.map((folder) => (
            <SidebarItem
              key={folder.id}
              active={selectedFolderId === folder.id}
              icon={<Folder className="h-4 w-4" />}
              label={folder.name}
              count={folder.project_count}
              onClick={() => onSelect(folder.id)}
            />
          ))
        )}
      </div>

      <Separator />
      <div className="p-3">
        <Button asChild variant="ghost" size="sm" className="w-full justify-start">
          <Link to="/settings">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </Link>
        </Button>
      </div>
    </aside>
  );
}

interface SidebarItemProps {
  active: boolean;
  icon: JSX.Element;
  label: string;
  count: number;
  onClick: () => void;
  disabled?: boolean;
}

function SidebarItem({
  active,
  icon,
  label,
  count,
  onClick,
  disabled,
}: SidebarItemProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
        active ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
      } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <span className="text-muted-foreground">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {count > 0 ? (
        <span className="ml-auto text-xs text-muted-foreground">{count}</span>
      ) : (
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" />
      )}
    </button>
  );
}
